import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { MailProcessing } from '../features/information-extraction/db/MailExtractionResult.js';

/** Sentinel used in the `evaluations` multiselect for "not yet evaluated" (evaluation null/absent). */
export const NOT_EVALUATED_FILTER = '__not_evaluated__';

@Entity()
export class MailboxFilter extends AdornisFilter {
  static override _class = 'MailboxFilter';

  // Basic product identification
  @Field(() => String) sapNumber?: Maybe<string>;
  @Field(() => Boolean) hasComments?: Maybe<boolean>;
  @Field(() => Boolean) validationFailed?: Maybe<boolean>;

  // Multiselect over MailProcessing.evaluation values (plus NOT_EVALUATED_FILTER
  // for null/absent). Empty/undefined means "no evaluation filter".
  @Field(() => [String]) evaluations?: Maybe<string[]>;

  // Me-mode: when set, only mails whose sender address equals this address are
  // shown. `mineOnly` is the client intent; the server resolves the logged-in
  // user's e-mail into `senderAddress` so the client cannot forget/spoof it.
  @Field(() => Boolean) mineOnly?: Maybe<boolean>;
  @Field(() => String) senderAddress?: Maybe<string>;

  // Archived mails are hidden by default (moved out of the INBOX). Set to show them.
  @Field(() => Boolean) showArchived?: Maybe<boolean>;

  constructor(data: Partial<MailboxFilter>) {
    super(data);
  }

  override getMongoAggregationFilter() {
    const pipeline: any[] = [];

    // add data
    pipeline.push({
      $lookup: {
        from: MailProcessing._collectionName,
        localField: '_id',
        foreignField: 'mailID',
        as: 'mailProcessings',
      },
    });

    pipeline.push({ $match: this.getMongoFilter() });

    // hide archived mails unless explicitly requested (matches missing + null)
    if (!this.showArchived) {
      pipeline.push({ $match: { archivedAt: null } });
    }

    // An employee's answer to a Rückfrage lands in the same mailbox as the inquiries, but it
    // is correspondence about a request, not a request. Always hidden — showing it would put
    // a mail reading "1: A" into the list as if it were something to quote for.
    pipeline.push({ $match: { notificationReplyFor: null } });

    // me-mode: restrict to mails sent by the given address (case-insensitive,
    // exact match against any of the `from` addresses)
    if (this.senderAddress) {
      const escaped = this.senderAddress.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      pipeline.push({
        $match: { 'from.address': { $regex: `^${escaped}$`, $options: 'i' } },
      });
    }

    // sap number filter
    if (this.sapNumber) {
      pipeline.push({
        $match: { 'mailProcessings.sapNumber': { $regex: `.*${this.sapNumber}.*`, $options: 'i' } },
      });
    }

    // has comments filter
    if (this.hasComments) {
      pipeline.push({
        $match: {
          'mailProcessings.notes': { $exists: true, $type: 'array', $ne: [] },
        },
      });
    }

    // validation failed filter (offers blocked from SAP due to missing mandatory fields)
    if (this.validationFailed) {
      pipeline.push({
        $match: { 'mailProcessings.sapValidation.ok': false },
      });
    }

    // evaluation multiselect: match any of the selected ratings. NOT_EVALUATED_FILTER
    // maps to a null/absent evaluation (also catches mails without a processing yet).
    if (this.evaluations?.length) {
      const concrete = this.evaluations.filter(e => e !== NOT_EVALUATED_FILTER);
      const ors: any[] = [];
      if (concrete.length) ors.push({ 'mailProcessings.evaluation': { $in: concrete } });
      if (this.evaluations.includes(NOT_EVALUATED_FILTER)) {
        ors.push({ 'mailProcessings.evaluation': { $in: [null] } });
      }
      if (ors.length) pipeline.push({ $match: ors.length === 1 ? ors[0] : { $or: ors } });
    }

    // universal search filter
    if (this.search) {
      pipeline.push({ $match: { $or: this.getSearchOrConditions(this.search) } });
    }
    pipeline.push({ $sort: this.getMongoOptions().sort });
    return pipeline;
  }

  /** The `$or` branches for the universal search box (subject, products, companies). */
  private getSearchOrConditions(search: string): object[] {
    const like = { $regex: `.*${search}.*`, $options: 'i' };
    return [
      { subject: like },
      { 'mailProcessings.extractionResult.extractedProducts.productNumber': like },
      { 'mailProcessings.extractionResult.extractedProducts.material': like },
      { 'mailProcessings.extractionResult.extractedProducts.productGroup': like },
      // requesting company
      { 'mailProcessings.extractionResult.requestingCompany.name': like },
      { 'mailProcessings.extractionResult.requestingCompany.address': like },
      { 'mailProcessings.extractionResult.requestingCompany.city': like },
      { 'mailProcessings.extractionResult.requestingCompany.zipCode': like },
      { 'mailProcessings.extractionResult.requestingCompany.email': like },
      { 'mailProcessings.extractionResult.requestingCompany.website': like },
      { 'mailProcessings.extractionResult.requestingCompany.debitor': like },
    ];
  }
}
